/**
 * ARC-14 — expense write guard (pure; no Firestore, no React).
 *
 * ROOT CAUSE THIS EXISTS FOR
 * The dashboard persists the WHOLE financial document from React state
 * (`authService.saveFinancialData({ expenseColumns, expenseData, ... })`, which
 * lands as `updateDoc(doc, {...data})`). `expenseColumns` and `expenseData` are
 * top-level fields, so each write REPLACES them wholesale — there is no
 * field-level merge and no concurrency check. Any session still holding a
 * snapshot taken before another session added columns will, on its next save,
 * write that older array back and delete the newer columns along with the rows'
 * values for them. That is the "three user-defined columns disappeared and June
 * reverted" signature.
 *
 * The fix is to stop writing the client snapshot blind. The save path reads the
 * server's current expense state immediately before writing and runs the
 * three-way merge below:
 *
 *   base   — expense state this session last saw CONFIRMED by the server
 *   client — what this session is about to write
 *   server — what is on the server right now
 *
 * Rules (all derived from "this session may only speak for what it knows"):
 *   - A column on the server that this session has never seen (absent from both
 *     `client` and `base`) was added elsewhere → keep it.
 *   - A column in `base` but not in `client` was removed here on purpose → drop it.
 *   - A row this session did not change (client row == base row) → take the
 *     server's row; another session may have newer values for it.
 *   - A row this session did change → its values win for the columns it knows,
 *     and any server-only column value on that row is carried through untouched.
 *   - A month present on the server but never seen here → keep it.
 *
 * Concurrent edits to the same month AND the same column still resolve
 * last-write-wins; that is a genuine conflict, not a silent structural loss.
 */

/** Row keys that are not expense columns. */
const META_KEYS = ["month", "total", "items"];

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
const num = (v) => Number(v) || 0;

/**
 * Stable per-row identity. Rows are identified by month; a month repeated in the
 * same table (e.g. two "Oct" rows) is disambiguated by occurrence so the nth
 * "Oct" here lines up with the nth "Oct" there. Rows with no month fall back to
 * position.
 */
const rowKeys = (rows) => {
  const seen = new Map();
  return asArray(rows).map((row, idx) => {
    const month = typeof row?.month === "string" ? row.month.trim() : "";
    if (!month) return `i:${idx}`;
    const n = seen.get(month) || 0;
    seen.set(month, n + 1);
    return `m:${month}#${n}`;
  });
};

const indexRows = (rows) => {
  const keys = rowKeys(rows);
  const map = new Map();
  asArray(rows).forEach((row, idx) => map.set(keys[idx], row));
  return map;
};

/** JSON.stringify with deterministic key order, so key ordering is not a "change". */
export const stableStringify = (value) => {
  if (value === null || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
};

/**
 * Has the member changed the expense buffer since the last server-confirmed
 * state? `committed` being absent means nothing has been confirmed yet — treat
 * that as "not dirty" so we never warn a member about edits they did not make.
 */
export const hasUnsavedExpenseChanges = (current, committed) => {
  if (!committed) return false;
  const shape = (s) => ({
    columns: asArray(s?.expenseColumns),
    rows: asArray(s?.expenseData),
  });
  return stableStringify(shape(current)) !== stableStringify(shape(committed));
};

/** Recompute a row total the same way the dashboard's recalc effect does. */
export const rowTotal = (row, columns) =>
  asArray(columns).reduce((sum, col) => sum + num(row?.[col]), 0);

/**
 * Column merge. Client order and deliberate removals are preserved; columns this
 * session never knew about are appended.
 */
export const reconcileExpenseColumns = ({ base, client, server }) => {
  const baseCols = asArray(base);
  const clientCols = asArray(client);
  const columns = [...clientCols];
  const recovered = [];

  asArray(server).forEach((col) => {
    if (typeof col !== "string") return;
    if (columns.includes(col)) return;
    if (baseCols.includes(col)) return; // removed here on purpose — respect it
    columns.push(col);
    recovered.push(col);
  });

  return { columns, recovered };
};

const rowChangedHere = (clientRow, baseRow, knownKeys) => {
  if (!baseRow) return true; // month added in this session
  for (const key of knownKeys) {
    if (key === "total") continue; // derived
    if (key === "items") {
      if (stableStringify(asObject(clientRow?.items)) !== stableStringify(asObject(baseRow?.items))) {
        return true;
      }
      continue;
    }
    if (key === "month") {
      if ((clientRow?.month ?? "") !== (baseRow?.month ?? "")) return true;
      continue;
    }
    if (num(clientRow?.[key]) !== num(baseRow?.[key])) return true;
  }
  return false;
};

/** Carry through every server field this session has no knowledge of. */
const carryUnknownFields = (clientRow, serverRow, knownKeys) => {
  const out = { ...clientRow };

  Object.keys(serverRow).forEach((key) => {
    if (key === "items") return;
    if (knownKeys.has(key)) return;
    if (key in out) return;
    out[key] = serverRow[key];
  });

  const serverItems = asObject(serverRow.items);
  const unknownItems = Object.keys(serverItems).filter(
    (field) => !knownKeys.has(field) && !(field in asObject(clientRow.items))
  );
  if (unknownItems.length) {
    out.items = { ...asObject(clientRow.items) };
    unknownItems.forEach((field) => {
      out.items[field] = serverItems[field];
    });
  }

  return out;
};

/**
 * Three-way merge of the expense payload about to be written.
 *
 * @returns {{expenseColumns: string[], expenseData: object[], recoveredColumns: string[],
 *   recoveredMonths: string[], serverPreferredRows: number, reconciled: boolean}}
 *   `reconciled` is true when the merged payload differs from what the client
 *   would have written on its own — i.e. a stale overwrite was prevented.
 */
export const reconcileExpenseWrite = ({ base, client, server } = {}) => {
  const clientColumns = asArray(client?.expenseColumns);
  const clientRows = asArray(client?.expenseData);

  // No server state to protect (fresh document, or the read failed): write ours.
  if (!server || (!server.expenseColumns && !server.expenseData)) {
    return {
      expenseColumns: clientColumns,
      expenseData: clientRows.map((row) => ({ ...row, total: rowTotal(row, clientColumns) })),
      recoveredColumns: [],
      recoveredMonths: [],
      serverPreferredRows: 0,
      reconciled: false,
    };
  }

  const baseColumns = asArray(base?.expenseColumns);
  const baseRows = asArray(base?.expenseData);
  const serverRows = asArray(server?.expenseData);

  const { columns, recovered: recoveredColumns } = reconcileExpenseColumns({
    base: baseColumns,
    client: clientColumns,
    server: server?.expenseColumns,
  });

  const knownKeys = new Set([...baseColumns, ...clientColumns, ...META_KEYS]);
  const baseIndex = indexRows(baseRows);
  const serverIndex = indexRows(serverRows);
  const clientKeys = rowKeys(clientRows);

  const recoveredMonths = [];
  let serverPreferredRows = 0;

  const merged = clientRows.map((clientRow, idx) => {
    const key = clientKeys[idx];
    const serverRow = serverIndex.get(key);
    if (!serverRow) return { ...clientRow };

    if (!rowChangedHere(clientRow, baseIndex.get(key), knownKeys)) {
      // Untouched here — the server's copy is at least as fresh as ours.
      serverPreferredRows += 1;
      return { ...serverRow };
    }
    return carryUnknownFields(clientRow, serverRow, knownKeys);
  });

  // Months added by another session after our snapshot. (A month in `base` but
  // not in `client` was deleted here on purpose and stays deleted.)
  const seenHere = new Set([...clientKeys, ...baseIndex.keys()]);
  const serverKeys = rowKeys(serverRows);
  serverRows.forEach((serverRow, idx) => {
    const key = serverKeys[idx];
    if (seenHere.has(key)) return;
    merged.push({ ...serverRow });
    recoveredMonths.push(serverRow?.month ?? key);
  });

  const expenseData = merged.map((row) => ({ ...row, total: rowTotal(row, columns) }));

  const clientOnly = {
    expenseColumns: clientColumns,
    expenseData: clientRows.map((row) => ({ ...row, total: rowTotal(row, clientColumns) })),
  };
  const reconciled =
    stableStringify({ expenseColumns: columns, expenseData }) !== stableStringify(clientOnly);

  return {
    expenseColumns: columns,
    expenseData,
    recoveredColumns,
    recoveredMonths,
    serverPreferredRows,
    reconciled,
  };
};

export default reconcileExpenseWrite;
